// =====================================================================
// PatientPhoto (PME Sprint 3a / S52) — avatar do paciente com foto real.
// Busca a imagem como BLOB (com token) e exibe via objectURL (LGPD: serving
// auth-gated, nada de URL pública). Modo `editable` mostra trocar/remover.
// Reusável: prancheta (Resumo), preview da lista, cabeçalho do Cadastro.
// =====================================================================
import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Avatar, IconButton, CircularProgress, Tooltip, Dialog } from "@material-ui/core";
import PersonIcon from "@material-ui/icons/Person";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import DeleteIcon from "@material-ui/icons/Delete";
import { toast } from "react-toastify";

import {
  uploadPatientPhoto,
  getPatientPhotoObjectUrl,
  deletePatientPhoto,
  validatePhotoFile,
  photoErrorMessage,
  PHOTO_ACCEPT
} from "../../services/fileService";
import useIsMobile from "../../utils/useIsMobile";

const useStyles = makeStyles((theme) => ({
  wrap: {
    position: "relative",
    flexShrink: 0
  },
  avatar: {
    background: theme.palette.primary.main,
    border: `2px solid ${theme.palette.divider}`
  },
  spinner: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    color: "#fff"
  },
  editBtn: {
    position: "absolute",
    right: -6,
    bottom: -6,
    background: theme.palette.primary.main,
    color: "#fff",
    padding: 6,
    "&:hover": { background: theme.palette.primary.dark }
  },
  editBtnWrap: { display: "inline" },
  removeBtn: {
    position: "absolute",
    right: -6,
    top: -6,
    background: theme.palette.error.main,
    color: "#fff",
    padding: 4,
    "&:hover": { background: theme.palette.error.dark }
  }
}));

const PatientPhoto = ({ patientId, photoUrl, size = 96, editable = false, onChange }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const [objUrl, setObjUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(false);
  const inputRef = useRef(null);
  const urlRef = useRef(null); // objectURL atual (pra revogar)

  // Carrega/atualiza a imagem quando muda o paciente ou o photoUrl.
  useEffect(() => {
    let active = true;
    const revoke = () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };

    if (!patientId || !photoUrl) {
      revoke();
      setObjUrl(null);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    getPatientPhotoObjectUrl(patientId)
      .then((url) => {
        if (!active) {
          URL.revokeObjectURL(url);
          return;
        }
        revoke();
        urlRef.current = url;
        setObjUrl(url);
      })
      .catch(() => {
        if (active) setObjUrl(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [patientId, photoUrl]);

  // Revoga o objectURL ao desmontar (evita vazamento de memória).
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    []
  );

  const handlePick = () => {
    if (inputRef.current) inputRef.current.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = ""; // permite re-selecionar o mesmo arquivo
    if (!file) return;
    const verr = validatePhotoFile(file);
    if (verr) {
      toast.error(verr);
      return;
    }
    setBusy(true);
    try {
      const updated = await uploadPatientPhoto(patientId, file);
      toast.success("Foto atualizada.");
      if (onChange) onChange(updated);
    } catch (err) {
      toast.error(photoErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (busy) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm("Remover a foto do paciente?")) return;
    setBusy(true);
    try {
      await deletePatientPhoto(patientId);
      toast.success("Foto removida.");
      if (onChange) onChange(null);
    } catch (err) {
      toast.error(photoErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const hasPhoto = !!objUrl;

  return (
    <div className={classes.wrap} style={{ width: size, height: size }}>
      <Avatar
        src={objUrl || undefined}
        className={classes.avatar}
        style={{ width: size, height: size, cursor: hasPhoto ? "zoom-in" : "default" }}
        imgProps={{ style: { objectFit: "cover", objectPosition: "top" } }}
        onClick={() => hasPhoto && setZoom(true)}
        title={hasPhoto ? "Ver foto ampliada" : undefined}
      >
        {!hasPhoto && <PersonIcon style={{ fontSize: Math.round(size * 0.55) }} />}
      </Avatar>

      {loading && <CircularProgress size={Math.round(size * 0.4)} className={classes.spinner} />}

      {editable && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={PHOTO_ACCEPT}
            style={{ display: "none" }}
            onChange={handleFile}
          />
          <Tooltip title={hasPhoto ? "Trocar foto" : "Adicionar foto"}>
            <span className={classes.editBtnWrap}>
              <IconButton size="small" className={classes.editBtn} onClick={handlePick} disabled={busy}>
                {busy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PhotoCameraIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          {hasPhoto && !busy && (
            <Tooltip title="Remover foto">
              <IconButton size="small" className={classes.removeBtn} onClick={handleRemove}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}

      {/* Lightbox: clique no avatar abre a foto INTEIRA (sem corte) — pra ver o
          paciente da cintura pra cima, nao so o rosto recortado no circulo. */}
      {zoom && objUrl && (
        <Dialog open onClose={() => setZoom(false)} maxWidth="md" fullScreen={isMobile}>
          <img
            src={objUrl}
            alt="Foto do paciente"
            onClick={() => setZoom(false)}
            style={{
              display: "block",
              maxWidth: "90vw",
              maxHeight: "85vh",
              objectFit: "contain",
              cursor: "zoom-out"
            }}
          />
        </Dialog>
      )}
    </div>
  );
};

export default PatientPhoto;
